/**
 * Footer component with organization credits
 */

interface FooterProps {
  minimal?: boolean;
}

export function Footer({ minimal = false }: FooterProps) {
  const currentYear = new Date().getFullYear();
  
  if (minimal) {
    return (
      <footer className="border-t border-surface-800 py-6 bg-surface-900/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-surface-500 text-sm">
              Data: Federation Account Allocation Committee (FAAC) • 2007–2025
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="#about" 
                className="text-surface-400 hover:text-accent-400 transition-colors text-sm"
              >
                About
              </a>
              <span className="text-surface-600">•</span>
              <span className="text-surface-500 text-sm">
                Built by{' '}
                <a 
                  href="https://dumbledoretech.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent-400 hover:text-accent-300 transition-colors"
                >
                  Dumbledore
                </a>
                {' '}&{' '}
                <a 
                  href="https://dataphyte.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent-400 hover:text-accent-300 transition-colors"
                >
                  Dataphyte
                </a>
              </span>
            </div>
          </div>
        </div>
      </footer>
    );
  }
  
  return (
    <footer className="border-t border-surface-800 bg-gradient-to-b from-surface-900 to-surface-950">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* About the project */}
          <div className="space-y-4">
            <h3 className="text-lg font-display font-bold text-surface-100">
              FAAC<span className="text-accent-500">viz</span>
            </h3>
            <p className="text-surface-400 text-sm leading-relaxed">
              An interactive visualization of Nigeria's Federation Account Allocation Committee (FAAC) 
              data from 2007 to 2025. Explore how federal allocations are distributed across 
              Nigeria's 36 states and the FCT.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-xs text-surface-500">Data Source:</span>
              <span className="text-xs text-surface-400">FAAC Official Records</span>
            </div>
          </div>
          
          {/* Built by */}
          <div className="space-y-6">
            <h4 className="text-sm font-semibold text-surface-300 uppercase tracking-wider">
              Built by
            </h4>
            
            {/* Dumbledore Solutions */}
            <a 
              href="https://dumbledoretech.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-3 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-accent-500/30 hover:bg-surface-800 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-surface-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                <img 
                  src="/dumbledore-logo.svg" 
                  alt="Dumbledore Solutions" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="font-semibold text-surface-100 group-hover:text-accent-400 transition-colors">
                  Dumbledore Solutions
                </h5>
                <p className="text-xs text-surface-500 truncate">
                  Software Development & AI Integration
                </p>
              </div>
              <svg className="w-4 h-4 text-surface-500 group-hover:text-accent-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            
            {/* Dataphyte */}
            <a 
              href="https://dataphyte.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-3 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-accent-500/30 hover:bg-surface-800 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                <img 
                  src="/dataphyte.png" 
                  alt="Dataphyte" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="font-semibold text-surface-100 group-hover:text-accent-400 transition-colors">
                  Dataphyte
                </h5>
                <p className="text-xs text-surface-500 truncate">
                  Data for Inclusive Growth
                </p>
              </div>
              <svg className="w-4 h-4 text-surface-500 group-hover:text-accent-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          
          {/* Links */}
          <div className="space-y-6">
            <h4 className="text-sm font-semibold text-surface-300 uppercase tracking-wider">
              Explore
            </h4>
            <nav className="flex flex-col gap-3">
              <a 
                href="#" 
                className="text-surface-400 hover:text-accent-400 transition-colors text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Data Explorer
              </a>
              <a 
                href="#story" 
                className="text-surface-400 hover:text-accent-400 transition-colors text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Data Story
              </a>
              <a 
                href="#about" 
                className="text-surface-400 hover:text-accent-400 transition-colors text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                About
              </a>
            </nav>
            
            {/* Tech stack */}
            <div className="pt-4 border-t border-surface-800">
              <p className="text-xs text-surface-500 mb-2">Built with</p>
              <div className="flex flex-wrap gap-2">
                {['React', 'D3.js', 'Leaflet', 'GSAP', 'Tailwind'].map((tech) => (
                  <span 
                    key={tech}
                    className="px-2 py-1 text-xs bg-surface-800 text-surface-400 rounded-md"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright bar */}
      <div className="border-t border-surface-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-surface-500">
            <p>© {currentYear} FAAC Visualization Project. All rights reserved.</p>
            <p>
              {/* A collaboration between{' '} */}
              <a 
                href="https://dumbledoretech.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-400 hover:text-accent-300"
              >
                Dumbledore Solutions
              </a>
              {' '}&{' '}
              <a 
                href="https://dataphyte.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-400 hover:text-accent-300"
              >
                Dataphyte
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;

